import { Box, Button, MenuItem, Select, Typography, styled } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  resetExecutionReport,
  setSelectedComponent,
  setSelectedProduct,
  setSelectedTestCase,
  setSelectedTestCaseName,
} from "../../Redux/ExecutionReport/ExecutionReport";
import AutocompleteComponent from "../../components-new/common/AutoCompleteComponent";
import { DeleteIcon } from "../../components-new/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { HelpIconButton } from "../../components-new/help/help-modal";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: "300px",
    },
  },
};

const StyledSelect = styled(Select)`
  border-radius: 5px;
  border: 1px solid #d1dbff;
  background-color: #ffffff;
  font-size: 16px;
  color: #2b3539;
  min-width: 200px;
  max-height: 300px;

  .MuiSelect-select {
    padding: 13px 15px;
  }
`;

const ReportSelection = ({
  executionReportFetch,
  setIsFiltered,
  testStepData,
  products,
  modules,
  refetchTestCases,
  testCaseSearchKeyword,
  setTestCaseSearchKeyword,
  testCases,
  setRefreshFlag,
  removeheader,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const [internalProduct, setInternalProduct] = useState("");
  const selectedProduct = useSelector((state) => state.executionSlice.selectedProduct);
  const selectedModule = useSelector((state) => state.executionSlice.selectedComponent);
  const selectedTestCase = useSelector((state) => state.executionSlice.selectedTestCase);
  const selectedTestCaseName = useSelector((state) => state.executionSlice.selectedTestCaseName);

  const queryParams = new URLSearchParams(location.search);
  const queryClient = useQueryClient();

  useEffect(() => {
    setInternalProduct(selectedProduct);
  }, [selectedProduct]);

  const handleProductChange = (event) => {
    setRefreshFlag(false);
    setInternalProduct(event.target.value);
    setIsFiltered(false);
    Promise.resolve().then(() => {
      dispatch(setSelectedProduct(event.target.value));
    });
  };

  const handleSearchClick = async () => {
    refetchTestCases(selectedModule, testCaseSearchKeyword);
    executionReportFetch();
    setIsFiltered(true);
  };

  const handleOptionChange = (event, value, reason) => {
    setTestCaseSearchKeyword(value);
    if (reason === "input" && selectedTestCase) {
      dispatch(setSelectedTestCase(null));
      dispatch(setSelectedTestCaseName(""));
    }
  };

  const handleTestCaseChange = (event, newValue, reason) => {
    // only update selection if user explicitly selected from dropdown
    if (reason === "selectOption" && newValue && typeof newValue === "object") {
      setIsFiltered(true);
      removeheader();
      dispatch(setSelectedTestCase(newValue.idtest_case));
      dispatch(setSelectedTestCaseName(newValue.Test_Case));
      setTestCaseSearchKeyword(newValue.Test_Case || "");
      executionReportFetch();
    } else if (reason === "clear" || (reason === "selectOption" && !newValue)) {
      setIsFiltered(true);
      removeheader();
      dispatch(setSelectedTestCase(null));
      dispatch(setSelectedTestCaseName(""));
      setTestCaseSearchKeyword("");
      executionReportFetch();
    }
  };

  const handleClearFilter = () => {
    removeheader();
    setIsFiltered(false);
    setRefreshFlag(false);
    setTestCaseSearchKeyword("");
    queryParams.delete("tcrid");
    navigate(
      { hash: queryParams.toString() ? `#${queryParams.toString()}` : "" },
      { replace: true },
    );
    dispatch(resetExecutionReport());
  };

  return (
    <>
      <Box>
        <Box>
          <Typography color="#8089A8" fontSize="13px" fontWeight={500}>
            Application
          </Typography>
        </Box>
        <StyledSelect
          MenuProps={MenuProps}
          value={selectedProduct}
          onChange={(event) => {
            handleProductChange(event);
          }}
          displayEmpty
        >
          <MenuItem disabled value="">
            Application
          </MenuItem>
          {products &&
            products.length > 0 &&
            (products ?? [{ label: "", value: "" }]).map((product) => (
              <MenuItem value={product.idproducts} key={product.idproducts}>
                {product.productname}
              </MenuItem>
            ))}
        </StyledSelect>
      </Box>
      {selectedProduct && (
        <Box>
          <Box>
            <Typography color="#8089A8" fontSize="13px" fontWeight={500}>
              Module
            </Typography>
          </Box>
          <StyledSelect
            MenuProps={MenuProps}
            value={selectedModule}
            displayEmpty
            onChange={(event) => {
              setRefreshFlag(false);

              dispatch(setSelectedComponent(event.target.value));
              setIsFiltered(false);
            }}
            disabled={!modules}
          >
            <MenuItem disabled value="">
              Module
            </MenuItem>
            {modules &&
              modules.length > 0 &&
              modules.map((module) => (
                <MenuItem key={module.idmodules} value={module.idmodules}>
                  {module.modulename}
                </MenuItem>
              ))}
          </StyledSelect>
        </Box>
      )}

      {selectedModule && (
        <Box>
          <Box>
            <Typography color="#8089A8" fontSize="13px" fontWeight={500}>
              Test Scenario
            </Typography>
          </Box>
          <AutocompleteComponent
            disabled={!selectedModule}
            options={testCases ?? []}
            value={
              selectedTestCase && testCases?.length
                ? (() => {
                    const selectedTC = testCases.find(
                      (testCase) => testCase.idtest_case === selectedTestCase,
                    );
                    // only show the selected value if the input matches the test case name
                    // this prevents auto-reset when user is typing a different search
                    if (selectedTC && testCaseSearchKeyword === selectedTC.Test_Case) {
                      return selectedTC;
                    }
                    return null;
                  })()
                : null
            }
            inputValue={testCaseSearchKeyword}
            onChange={handleTestCaseChange}
            onInputChange={handleOptionChange}
            getOptionLabel={(testCase) => testCase.Test_Case ?? ""}
            placeholder="Search Test Cases"
          />
        </Box>
      )}
      <Button
        variant="outlined"
        startIcon={<DeleteIcon />}
        size="medium"
        onClick={handleClearFilter}
        sx={{
          color: "#828286",
          background: "#ffffff",
          height: "55px",
          border: "1px solid #E2E6F3",
          outline: 0,
          textTransform: "capitalize",
          mt: 2.5,
        }}
      >
        Clear filter
      </Button>
      <div className="mt-4">
        <HelpIconButton path={path} />
      </div>
    </>
  );
};

export default ReportSelection;
